import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { initializeSocket, disconnectSocket, getSocket, isSocketConnected, joinRoom, leaveRoom } from '../services/socket';

/**
 * useSocketEvent - Listen for a specific socket event
 * Automatically connects with auth token and cleans up on unmount
 *
 * @param {string} event - Socket event name
 * @param {function} handler - Callback when event fires
 * @param {boolean} enabled - Whether to listen (default: true)
 */
export const useSocketEvent = (event, handler, enabled = true) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const { token } = useAuth();
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!token || !enabled) return;

    const socket = initializeSocket(token);

    const wrappedHandler = (...args) => {
      handlerRef.current(...args);
    };

    socket.on(event, wrappedHandler);

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off(event, wrappedHandler);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [token, event, enabled]);

  return { connected };
};

/**
 * useSocketRoom - Join a room and listen for multiple events
 *
 * @param {string} room - Room name to join
 * @param {object} events - { eventName: handler, ... }
 */
export const useSocketRoom = (room, events = {}) => {
  const { token } = useAuth();
  const eventsRef = useRef(events);
  eventsRef.current = events;

  useEffect(() => {
    if (!token) return;

    const socket = initializeSocket(token);
    joinRoom(room);

    const cleanups = Object.entries(eventsRef.current).map(([event, handler]) => {
      const wrappedHandler = (...args) => handler(...args);
      socket.on(event, wrappedHandler);
      return () => socket.off(event, wrappedHandler);
    });

    return () => {
      leaveRoom(room);
      cleanups.forEach(fn => fn());
    };
  }, [token, room]);

  return { connected: isSocketConnected() };
};

export default useSocketEvent;